import 'react-toastify/dist/ReactToastify.min.css';

import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ToastOptions } from 'react-toastify';
import { Flip, toast, ToastContainer } from 'react-toastify';

type ToastType = 'success' | 'info' | 'error' | 'warning';

export type IToastContext = {
  /**
   * Show a toast message
   * @param msg - The message to show
   * @param type - The type of the toast, default is 'success'
   * @returns void
   * @example
   * showToast('Success toast is default');
   * showToast('Information toast', 'info');
   * showToast('Error toast', 'error');
   * showToast(<div>ReactNode toast</div>);
   * */
  showToast: (
    msg: string | ReactNode,
    type?: ToastType,
    options?: Pick<ToastOptions, 'autoClose' | 'toastId'>,
  ) => void;
};

const initToastContext: IToastContext = {
  showToast: () => {},
};

const ToastContext = createContext(initToastContext);

export const ToastProvider = ({ children }: { children: ReactNode }) => (
  <ToastContext.Provider
    value={useMemo<IToastContext>(
      () => ({
        showToast: (msg, type, options) =>
          toast(msg, {
            transition: Flip,
            type,
            theme: 'colored',
            ...options,
          }),
      }),
      [],
    )}
  >
    <ToastContainer
      position='bottom-right'
      autoClose={5000}
      newestOnTop={true}
      limit={8}
      closeOnClick={false}
      rtl={false}
      pauseOnFocusLoss
      draggable={false}
      pauseOnHover
      transition={Flip}
      theme='colored'
      style={{ zIndex: 10010 }}
    />
    {children}
  </ToastContext.Provider>
);

export const useToast = () => useContext(ToastContext);
